/**
 * DriverLayout - Layout mobile-first para o App do Motorista
 *
 * Caracteristicas:
 * - Design responsivo otimizado para mobile
 * - Bottom navigation
 * - Header compacto
 * - Suporte a PWA (fullscreen, safe areas)
 */

import { useState, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  SwipeableDrawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Home as HomeIcon,
  Route as RouteIcon,
  Notifications as NotificationsIcon,
  Person as ProfileIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Help as HelpIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useEffectAsync } from '../reactHelper';
import driverAssignmentService from '../routing/services/driverAssignmentService';

const DriverLayout = () => {
  const tokens = usePremiumTokens();
  const navigate = useNavigate();
  const location = useLocation();

  // Estado
  const [driverInfo, setDriverInfo] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [notificationCount] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Mapear path para indice do bottom nav
  const getNavValue = () => {
    const path = location.pathname;
    if (path.includes('/driver/routes')) return 1;
    if (path.includes('/driver/notifications')) return 2;
    if (path.includes('/driver/profile')) return 3;
    return 0;
  };

  // Carregar dados do motorista
  useEffectAsync(async () => {
    try {
      const driver = await driverAssignmentService.getCurrentDriverInfo();
      setDriverInfo(driver);

      if (driver) {
        // Buscar rotas pendentes
        const myRoutes = await driverAssignmentService.getMyRoutes();
        const pending = myRoutes.filter((r) => r.assignmentStatus === 'pending');
        setPendingCount(pending.length);
      }
    } catch (error) {
      console.error('[DriverLayout] Error loading driver info:', error);
    }
  }, []);

  // Handlers
  const handleNavChange = useCallback((event, newValue) => {
    const paths = ['/driver', '/driver/routes', '/driver/notifications', '/driver/profile'];
    navigate(paths[newValue]);
  }, [navigate]);

  const handleMenuOpen = useCallback((event) => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleLogout = useCallback(async () => {
    handleMenuClose();
    try {
      // Call logout API
      await fetch('/api/session', { method: 'DELETE' });
    } catch (error) {
      console.error('[DriverLayout] Logout API error:', error);
    } finally {
      // Clear all local storage
      localStorage.removeItem('driver_token');
      localStorage.removeItem('driver_info');
      localStorage.removeItem('fcm_token');
      localStorage.removeItem('route_cache');
      sessionStorage.clear();

      // Clear service worker cache if available
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => {
            if (name.includes('driver') || name.includes('route')) {
              caches.delete(name);
            }
          });
        });
      }

      // Navigate to login
      navigate('/login', { replace: true });
    }
  }, [navigate, handleMenuClose]);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        maxHeight: '100vh',
        backgroundColor: tokens?.surface?.base,
        // Suporte a safe areas do iOS
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          backgroundColor: tokens?.surface?.raised,
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => setDrawerOpen(true)}
            sx={{ color: tokens?.text?.secondary }}
          >
            <MenuIcon />
          </IconButton>
          <Box>
            <Typography
              sx={{
                color: tokens?.text?.primary,
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: 1.2,
              }}
            >
              Fleet Driver
            </Typography>
            {driverInfo && (
              <Typography
                sx={{
                  color: tokens?.text?.tertiary,
                  fontSize: '12px',
                }}
              >
                {driverInfo.name}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={handleRefresh}
            sx={{ color: tokens?.text?.tertiary }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ p: 0 }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: tokens?.accent?.primary,
                fontSize: '14px',
              }}
            >
              {driverInfo?.name?.[0]?.toUpperCase() || 'M'}
            </Avatar>
          </IconButton>
        </Box>

        {/* Menu do perfil */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              backgroundColor: tokens?.surface?.raised,
              minWidth: 200,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600 }}>
              {driverInfo?.name || 'Motorista'}
            </Typography>
            <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
              {driverInfo?.uniqueId || ''}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: tokens?.border?.subtle }} />
          <MenuItem onClick={() => { handleMenuClose(); navigate('/driver/profile'); }}>
            <ListItemIcon><ProfileIcon sx={{ color: tokens?.text?.secondary }} /></ListItemIcon>
            <ListItemText>Meu Perfil</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); navigate('/driver/settings'); }}>
            <ListItemIcon><SettingsIcon sx={{ color: tokens?.text?.secondary }} /></ListItemIcon>
            <ListItemText>Configuracoes</ListItemText>
          </MenuItem>
          <Divider sx={{ borderColor: tokens?.border?.subtle }} />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutIcon sx={{ color: tokens?.semantic?.error }} /></ListItemIcon>
            <ListItemText sx={{ color: tokens?.semantic?.error }}>Sair</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          pb: 8, // Espaco para bottom nav
        }}
      >
        <Outlet />
      </Box>

      {/* Bottom Navigation */}
      <BottomNavigation
        value={getNavValue()}
        onChange={handleNavChange}
        showLabels
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: tokens?.surface?.raised,
          borderTop: `1px solid ${tokens?.border?.subtle}`,
          height: 64,
          paddingBottom: 'env(safe-area-inset-bottom)',
          '& .MuiBottomNavigationAction-root': {
            color: tokens?.text?.tertiary,
            minWidth: 'auto',
            '&.Mui-selected': {
              color: tokens?.accent?.primary,
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '11px',
            '&.Mui-selected': {
              fontSize: '11px',
            },
          },
        }}
      >
        <BottomNavigationAction
          label="Inicio"
          icon={<HomeIcon />}
        />
        <BottomNavigationAction
          label="Rotas"
          icon={
            <Badge badgeContent={pendingCount} color="error">
              <RouteIcon />
            </Badge>
          }
        />
        <BottomNavigationAction
          label="Alertas"
          icon={
            <Badge badgeContent={notificationCount} color="error">
              <NotificationsIcon />
            </Badge>
          }
        />
        <BottomNavigationAction
          label="Perfil"
          icon={<ProfileIcon />}
        />
      </BottomNavigation>

      {/* Drawer lateral */}
      <SwipeableDrawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.raised,
            width: 280,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 700,
              fontSize: '20px',
              mb: 0.5,
            }}
          >
            Fleet Driver
          </Typography>
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
            v1.0.0
          </Typography>
        </Box>
        <Divider sx={{ borderColor: tokens?.border?.subtle }} />
        <List>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/driver'); }}>
            <ListItemIcon><HomeIcon sx={{ color: tokens?.text?.secondary }} /></ListItemIcon>
            <ListItemText primary="Inicio" />
          </ListItemButton>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/driver/routes'); }}>
            <ListItemIcon>
              <Badge badgeContent={pendingCount} color="error">
                <RouteIcon sx={{ color: tokens?.text?.secondary }} />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="Minhas Rotas" />
          </ListItemButton>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/driver/history'); }}>
            <ListItemIcon><RouteIcon sx={{ color: tokens?.text?.secondary }} /></ListItemIcon>
            <ListItemText primary="Historico" />
          </ListItemButton>
        </List>
        <Divider sx={{ borderColor: tokens?.border?.subtle }} />
        <List>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/driver/help'); }}>
            <ListItemIcon><HelpIcon sx={{ color: tokens?.text?.secondary }} /></ListItemIcon>
            <ListItemText primary="Ajuda" />
          </ListItemButton>
          <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/driver/settings'); }}>
            <ListItemIcon><SettingsIcon sx={{ color: tokens?.text?.secondary }} /></ListItemIcon>
            <ListItemText primary="Configuracoes" />
          </ListItemButton>
        </List>
      </SwipeableDrawer>
    </Box>
  );
};

export default DriverLayout;
